import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { gradients } from '@/utils/theme';

// Check if we're in development mode with mock auth
const isDevelopmentMode = import.meta.env.VITE_ENVIRONMENT === 'development';
const useMockAuth = isDevelopmentMode && (
  import.meta.env.VITE_USER_POOL_ID === 'us-east-1_mockpoolid' ||
  import.meta.env.VITE_DISABLE_AUTH === 'true'
);


// Mock credentials for development
const mockCredentials = [
  { role: 'Admin', email: 'admin@ibusiness.ai', password: 'admin123' },
  { role: 'Manager', email: 'manager@ibusiness.ai', password: 'manager123' },
  { role: 'Analyst', email: 'analyst@ibusiness.ai', password: 'analyst123' },
  { role: 'Viewer', email: 'viewer@ibusiness.ai', password: 'viewer123' },
];

const AuthForm: React.FC = () => {
  const { signIn, signUp, isLoading, error, clearError } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'viewer',
    department: '',
    confirmationCode: '',
  });


  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    clearError();
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (isSignUp) {
      if (!formData.firstName) {
        errors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        errors.lastName = 'Last name is required';
      }
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Confirm password is required';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      await signIn(formData.email, formData.password);
    } catch {
      // Error is handled by context
    }
  };

  const handleSignUp = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      await signUp(formData.email, formData.password, {
        given_name: formData.firstName,
        family_name: formData.lastName,
        email: formData.email
      });
    } catch {
      // Error is handled by context
    }
  };

  const handleQuickLogin = (email: string, password: string) => {
    setFormData(prev => ({ ...prev, email, password }));
    signIn(email, password).catch(() => {
      // Error is handled by context
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: gradients.background,
      }}
    >
      <Card 
        sx={{ 
          width: '100%', 
          maxWidth: 500,
          backdropFilter: 'blur(20px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            component="img"
            src="/iBusiness_logo.svg"
            alt="iBusiness Logo"
            sx={{
              height: '2rem',
              mb: 2,
              display: 'block',
              mx: 'auto'
            }}
          />
          <Typography 
            variant="subtitle1" 
            align="center" 
            color="textSecondary" 
            sx={{
              mb: 4,
              fontWeight: 700,
              background: gradients.secondary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Business Management Platform
          </Typography>

          {useMockAuth && !isSignUp && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                🔧 Development Mode - Quick Login
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {mockCredentials.map((cred) => (
                  <Chip
                    key={cred.role}
                    label={cred.role}
                    onClick={() => handleQuickLogin(cred.email, cred.password)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {error}
            </Alert>
          )}

          {/* Announce validation errors to screen readers */}
          {Object.keys(validationErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              Please fix the errors below and try again.
            </Alert>
          )}

          <form onSubmit={isSignUp ? handleSignUp : handleSignIn} noValidate>
            {isSignUp && (
              <>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  error={!!validationErrors.firstName}
                  helperText={validationErrors.firstName}
                  inputProps={{ 'aria-required': 'true' }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                  error={!!validationErrors.lastName}
                  helperText={validationErrors.lastName}
                  inputProps={{ 'aria-required': 'true' }}
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              inputProps={{ 'aria-required': 'true' }}
              required
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              inputProps={{ 'aria-required': 'true' }}
              sx={{ mb: isSignUp ? 3 : 4 }}
              required
            />

            {isSignUp && (
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                inputProps={{ 'aria-required': 'true' }}
                sx={{ mb: 3 }}
              />
            )}
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  mb: 2,
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : isSignUp ? 'Sign Up' : 'Sign In'}
              </Button>
            </Box>
          </form>

          {/* Toggle between sign in and sign up */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setValidationErrors({});
                clearError();
              }}
              sx={{ cursor: 'pointer' }}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </Link>
          </Box>

          {useMockAuth && !isSignUp && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  Mock Credentials
                </Typography>
              </Divider>
              <Stack spacing={1}>
                {mockCredentials.map((cred) => (
                  <Box key={cred.role} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">
                      <strong>{cred.role}:</strong> {cred.email}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {cred.password}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthForm;
